import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';

import { Input, Select } from 'common-src/components/base';
import { getRoleConfig } from 'common-src/features/auth';
import { apiRequest, getRestErrorMessage, HttpMethods } from 'common-src/features/rest';
import { getClientOptions, getClientsByRole, roles } from 'common-src/models/Client';
import { ReferralTypeOptions } from 'common-src/models/PatientReferral';
import { downloadPDF } from 'common-src/utils/pdfUtils';

import useCustomSelector from 'src/hooks/useCustomSelector';
import usePopup from 'src/hooks/usePopup';
import BasePopup from 'src/popups/BasePopup';

import { generatePDFName } from './helpers';
import styles from './SendReferralPopup.module.scss';

const SendReferralPopup = ({ patientId, onClose }) => {
  const [data, setData] = useState({
    referralType: null,
    customReferralType: '',
    diagnosisCode: '',
    diagnosisDescription: '',
    referredBy: null,
  });

  const canSelectReferredBy = useCustomSelector(
    (state) => getRoleConfig(state)?.patientChart?.actions?.canSelectReferredBy,
  );
  const clients = useCustomSelector((state) =>
    getClientsByRole(state, [roles.Physician.id, roles.Neurologist.id, roles.NPNeurologist.id]),
  );

  const clientOptions = useMemo(() => getClientOptions(clients, true), [clients]);

  const { renderButtons } = usePopup();

  const onSubmitHandler = (loadingCallback, successCallback, errorCallback) => {
    loadingCallback();

    const requestBody = {
      ...data,
      referralType: data.customReferralType || data.referralType,
    };
    delete requestBody.customReferralType;

    apiRequest({
      endpoint: `members/${patientId}/generateReferral`,
      queryParams: {},
      body: requestBody,
      method: HttpMethods.Post,
    }).then(async (res) => {
      if (!res.ok) {
        const err = await res.json();
        const errMsg = getRestErrorMessage(err) || '';
        errorCallback(errMsg);
        return;
      }

      downloadPDF(res, generatePDFName(data, patientId));

      successCallback('Referral generated!');
      onClose(true);
    });
  };

  const onChange = (field, value) => setData((prev) => ({ ...prev, [field]: value }));

  return (
    <BasePopup
      id="generate-referral"
      open
      onClose={onClose}
      title="Generate Referral"
      containerClassNames={[styles.modalContainer]}
    >
      <div className={styles.content}>
        <Select
          id="referral-type"
          placeholder="Select type"
          options={ReferralTypeOptions}
          value={ReferralTypeOptions.find((op) => op.value === data.referralType)}
          label="Referral Type"
          onChange={(option) => {
            onChange('referralType', option.value);
          }}
          size="small"
        />
        <Input
          id="custom-referral-type"
          label="Custom Referral Type"
          placeholder="Enter custom referral type (optional)"
          value={data.customReferralType}
          onTextChange={(value) => onChange('customReferralType', value)}
          size="small"
          tooltipText="If provided, this will be used instead of the dropdown selection above"
        />
        <Input
          id="diagnosis-code"
          label="Diagnosis Code"
          placeholder="e.g., R41.3 (defaults to R41.3 if empty)"
          value={data.diagnosisCode}
          onTextChange={(value) => onChange('diagnosisCode', value)}
          size="small"
        />
        <Input
          id="diagnosis-description"
          label="Diagnosis Description"
          placeholder="e.g., Other Amnesia (defaults to Other Amnesia if empty)"
          value={data.diagnosisDescription}
          onTextChange={(value) => onChange('diagnosisDescription', value)}
          size="small"
        />
        {canSelectReferredBy && (
          <Select
            id="referred-by-select"
            options={clientOptions}
            onChange={(option) => onChange('referredBy', option?.value ?? null)}
            value={clientOptions.find((op) => op.value === data.referredBy)}
            label="Referred By"
            tooltipText="If you don't select any referral, Avinash Ravilla, DO will be assigned as one."
            withError={false}
            size="small"
            placeholder="Select referral"
            isClearable
          />
        )}
      </div>
      {renderButtons({
        containerClassName: styles.buttonsContainer,
        submitButtonText: 'Download and Close',
        onClose,
        onSubmit: onSubmitHandler,
        isSubmitEnabled: Boolean(data.referralType || data.customReferralType),
      })}
    </BasePopup>
  );
};

SendReferralPopup.propTypes = {
  onClose: PropTypes.func,
  patientId: PropTypes.number,
};

export default SendReferralPopup;
